import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import ActionsMenu from "./ActionsMenu";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";

export default function FeeBook() {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const user_email = user?.user_email;

  const [form, setForm] = useState({
    college_id: "",
    fee_head_id: "",
    student_id: "",
    fee_amount: "",
    status: "ACTIVE",
    created_by: "",
  });

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fetch Data
  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}`);
      if (data.success) {
        const mappedRows = data.items.map((r) => ({ id: r.fee_book_id, ...r }));
        setAllRows(mappedRows);
        setRows(mappedRows);
        setRowCount(mappedRows.length);
      } else {
        showSnackbar(data.message || "Failed to load data", "error");
      }
    } catch (e) {
      console.error(e);
      showSnackbar(e?.response?.data?.message || "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!search) setRows(allRows);
    else {
      const s = search.toLowerCase();
      setRows(
        allRows.filter(
          (r) => r.student_rollno?.toLowerCase().includes(s) || r.fee_amount?.toString().includes(s)
        )
      );
    }
  }, [search, allRows]);

  // Open Add Dialog
  const handleOpenAdd = () => {
    setEditing(null);
    setForm({
      college_id: "",
      fee_head_id: "",
      student_id: "",
      fee_amount: "",
      status: "ACTIVE",
      created_by: "",
    });
    setOpen(true);
  };

  // Open Edit Dialog
  const handleOpenEdit = (row) => {
    // normalize editing object to always have `id` (DataGrid expects `id` but some paths may send fee_book_id)
    const editObj = { ...row, id: row.id ?? row.fee_book_id };
    setEditing(editObj);
    setForm({
      college_id: row.college_id,
      fee_head_id: row.fee_head_id,
      student_id: row.student_id,
      fee_amount: row.fee_amount,
      status: row.status,
      created_by: row.created_by,
    });
    setOpen(true);
  };

  // Delete Row
  const handleDelete = async (row) => {
    if (!window.confirm("Delete this fee book entry?")) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/${row.fee_book_id}`
      );
      fetchData();
      showSnackbar("Fee book entry deleted successfully", "success");
    } catch (e) {
      console.error(e);
      showSnackbar(e?.response?.data?.message || "Delete failed", "error");
    }
  };

  // Add/Edit Submit
  const handleSubmit = async () => {
    // basic validations
    if (!form.fee_head_id || !form.student_id || !form.fee_amount) {
      showSnackbar("fee_head_id, student_id and fee_amount are required", "warning");
      return;
    }

    const payload = {
      ...form,
      college_id: Number(collegeId),
      client_id: Number(clientId),
      created_by: user_email,
    };
    try {
      if (editing) {
        const editId = editing.id ?? editing.fee_book_id;
        await axios.put(
          `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/${editId}`,
          payload
        );
        showSnackbar("Fee book updated successfully", "success");
      } else {
        await axios.post(`${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}`, payload);
        showSnackbar("Fee book created successfully", "success");
      }
      setOpen(false);
      fetchData();
    } catch (e) {
      console.error(e);
      showSnackbar(e?.response?.data?.message || "Save failed", "error");
    }
  };

  // CSV Upload
  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/bulk`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      showSnackbar(data.message || "CSV uploaded successfully", "success");
      fetchData();
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Upload failed", "error");
    } finally {
      e.target.value = "";
    }
  };

  const columns = useMemo(
    () => [
      { field: "fee_book_id", headerName: "ID", flex: 0.5, minWidth: 50 },
      { field: "student_id", headerName: "Student ID", flex: 1, minWidth: 120 },
      { field: "student_rollno", headerName: "Roll No", flex: 1, minWidth: 120 },
      { field: "fee_head_id", headerName: "Fee Head ID", flex: 1, minWidth: 120 },
      { field: "fee_amount", headerName: "Amount", flex: 1, minWidth: 120 },
      { field: "balance", headerName: "Balance", flex: 1, minWidth: 120 },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 120,
        renderCell: (p) => (
          <span style={{ color: p.value === "ACTIVE" ? "green" : "red" }}>{p.value}</span>
        ),
      },
      { field: "created_by", headerName: "Created By", flex: 1, minWidth: 120 },
      {
        field: "created_on",
        headerName: "Created On",
        flex: 1.2,
        minWidth: 120,
        valueGetter: (p) => p.row.created_on?.slice(0, 10),
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        flex: 1,
        minWidth: 130,
        renderCell: (params) => (
          <ActionsMenu row={params.row} onEdit={handleOpenEdit} onDelete={handleDelete} />
        ),
      },
    ],
    []
  );

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            FEE BOOK
          </Typography>
          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            Actions
          </Button>
          <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#115293" },
                }}
                onClick={() => {
                  setAnchorEl(null);
                  handleOpenAdd();
                }}
              >
                ADD FEE BOOK
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: "#4caf50",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#357a38" },
                }}
                onClick={() => {
                  setAnchorEl(null);
                  const link = document.createElement("a");
                  link.href = `${process.env.PUBLIC_URL}/feebook.csv`;
                  link.setAttribute("download", "feebook.csv");
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  backgroundColor: "#ff9800",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c77700" },
                }}
              >
                UPLOAD CSV
                <input
                  hidden
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    setAnchorEl(null);
                    handleUploadCsv(e);
                  }}
                />
              </Button>
            </MenuItem>
          </Menu>
          <TextField
            size="small"
            placeholder="Search data"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => fetchData()}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            autoHeight
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            pagination
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Fee Book" : "Add Fee Book"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Fee Head ID"
                value={form.fee_head_id}
                onChange={(e) => setForm({ ...form, fee_head_id: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Student ID"
                value={form.student_id}
                onChange={(e) => setForm({ ...form, student_id: e.target.value })}
                required
                fullWidth
              />
            </Stack>
            <TextField
              label="Fee Amount"
              value={form.fee_amount}
              onChange={(e) => setForm({ ...form, fee_amount: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Status"
              margin="dense"
              required
              select
              fullWidth
              value={form.status}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="" disabled hidden>
                Select Status
              </option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              ml: 2,
              backgroundColor: "#ff9800",
              color: "#fff",
              "&:hover": { backgroundColor: "#c77700" },
            }}
          >
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
